<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { publicApi } from '../api'
import AppMark from '../components/AppMark.vue'
import ErrorState from '../components/ErrorState.vue'
import LoadingState from '../components/LoadingState.vue'
import SlidePreview from '../components/SlidePreview.vue'
import { preloadSlides } from '../media'
import type { PublicPresentation } from '../types'

const props = defineProps<{
  slug: string
}>()

const deck = ref<PublicPresentation | null>(null)
const loading = ref(true)
const error = ref('')
const current = ref(0)
let touchStartX = 0
let touchStartY = 0

const count = computed(() => deck.value?.doc.slides.length ?? 0)
const progress = computed(() => count.value ? ((current.value + 1) / count.value) * 100 : 0)

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    deck.value = await publicApi.get(props.slug)
    await preloadSlides(deck.value.assets.slides)
    document.title = `${deck.value.title} — GetSlides`
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Не удалось открыть презентацию.'
  } finally {
    loading.value = false
  }
}

function go(to: number): void {
  if (!count.value) return
  current.value = Math.max(0, Math.min(count.value - 1, to))
}

function onKeydown(event: KeyboardEvent): void {
  if (['ArrowRight', 'ArrowDown', 'PageDown', ' '].includes(event.key)) {
    event.preventDefault()
    go(current.value + 1)
  } else if (['ArrowLeft', 'ArrowUp', 'PageUp'].includes(event.key)) {
    event.preventDefault()
    go(current.value - 1)
  } else if (event.key === 'Home') go(0)
  else if (event.key === 'End') go(count.value - 1)
}

function onTouchStart(event: TouchEvent): void {
  touchStartX = event.touches[0]?.clientX ?? 0
  touchStartY = event.touches[0]?.clientY ?? 0
}

function onTouchEnd(event: TouchEvent): void {
  const touch = event.changedTouches[0]
  if (!touch) return
  const dx = touch.clientX - touchStartX
  const dy = touch.clientY - touchStartY
  if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return
  go(current.value + (dx < 0 ? 1 : -1))
}

onMounted(() => {
  void load()
  window.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <main class="public-page">
    <header class="public-header">
      <a href="/" class="brand">
        <AppMark />
        <span>GetSlides</span>
      </a>
      <div v-if="deck" class="public-header__actions">
        <a :href="deck.assets.pdf" class="button button--ghost" target="_blank">
          <span class="pdf-long-label">Скачать&nbsp;</span>PDF
        </a>
        <a :href="`/p/${slug}/show`" class="button button--dark">▶ Показать</a>
      </div>
    </header>

    <LoadingState v-if="loading" title="Загружаем презентацию" full />
    <div v-else-if="error || !deck" class="public-error">
      <ErrorState
        title="Презентация не открылась"
        :text="error || 'Такой презентации нет.'"
        @retry="load"
      />
    </div>

    <template v-else>
      <section class="public-deck-heading">
        <span class="eyebrow">Презентация</span>
        <h1>{{ deck.title }}</h1>
        <p>Листайте стрелками или свайпом.</p>
      </section>

      <section
        class="public-stage"
        aria-roledescription="карусель"
        :aria-label="`Слайд ${current + 1} из ${count}`"
        @touchstart.passive="onTouchStart"
        @touchend.passive="onTouchEnd"
      >
        <button
          type="button"
          class="stage-arrow stage-arrow--left"
          :disabled="current === 0"
          aria-label="Предыдущий слайд"
          @click="go(current - 1)"
        >←</button>
        <SlidePreview
          :src="deck.assets.slides[current] ?? null"
          :slide="deck.doc.slides[current] ?? null"
          :aspect="deck.doc.aspect"
          :alt="`Слайд ${current + 1}`"
          eager
        />
        <button
          type="button"
          class="stage-arrow stage-arrow--right"
          :disabled="current === count - 1"
          aria-label="Следующий слайд"
          @click="go(current + 1)"
        >→</button>
      </section>

      <div class="public-progress">
        <span>{{ String(current + 1).padStart(2, '0') }}</span>
        <div><i :style="{ width: `${progress}%` }"></i></div>
        <span>{{ String(count).padStart(2, '0') }}</span>
      </div>

      <nav class="public-thumbnails" aria-label="Все слайды">
        <button
          v-for="(slide, index) in deck.doc.slides"
          :key="slide.id"
          type="button"
          :class="{ 'is-selected': index === current }"
          :aria-label="`Открыть слайд ${index + 1}`"
          @click="go(index)"
        >
          <SlidePreview
            :src="deck.assets.slides[index] ?? null"
            :slide="slide"
            :aspect="deck.doc.aspect"
            :alt="`Слайд ${index + 1}`"
          />
          <span>{{ index + 1 }}</span>
        </button>
      </nav>

      <footer class="public-footer">
        <p>Сделано в GetSlides</p>
        <a href="/" class="text-button">Создать свою презентацию →</a>
      </footer>
    </template>
  </main>
</template>
